/**
 * Комплексная арифметика для геометрического ядра гиперболических карт.
 *
 * Точки модели Пуанкаре (диска) и координаты поверхности представляются
 * структурой `Complex` (`{ re, im }`). Все функции чистые и детерминированные.
 *
 * Требования: 4.1
 */

import type { Complex } from './map-types.js';

/** Комплексная единица `1 + 0i`. */
export const ONE: Complex = { re: 1, im: 0 };

/** Комплексный ноль `0 + 0i`. */
export const ZERO: Complex = { re: 0, im: 0 };

/** Мнимая единица `0 + 1i`. */
export const I: Complex = { re: 0, im: 1 };

/** Создать комплексное число из вещественного и мнимого компонентов. */
export function complex(re: number, im = 0): Complex {
  return { re, im };
}

/** Комплексное число из вещественного числа (`re + 0i`). */
export function fromReal(re: number): Complex {
  return { re, im: 0 };
}

/**
 * Комплексное число из полярного представления `r·e^{iθ}`.
 * @param r модуль (радиус)
 * @param theta аргумент (угол в радианах)
 */
export function fromPolar(r: number, theta: number): Complex {
  return { re: r * Math.cos(theta), im: r * Math.sin(theta) };
}

/** Сложение: `a + b`. */
export function add(a: Complex, b: Complex): Complex {
  return { re: a.re + b.re, im: a.im + b.im };
}

/** Вычитание: `a − b`. */
export function subtract(a: Complex, b: Complex): Complex {
  return { re: a.re - b.re, im: a.im - b.im };
}

/** Унарное отрицание: `−a`. */
export function negate(a: Complex): Complex {
  return { re: -a.re, im: -a.im };
}

/** Умножение: `a · b`. */
export function multiply(a: Complex, b: Complex): Complex {
  return {
    re: a.re * b.re - a.im * b.im,
    im: a.re * b.im + a.im * b.re,
  };
}

/** Умножение на вещественный скаляр: `s · a`. */
export function scale(a: Complex, s: number): Complex {
  return { re: a.re * s, im: a.im * s };
}

/** Комплексное сопряжение: `ā`. */
export function conjugate(a: Complex): Complex {
  return { re: a.re, im: -a.im };
}

/** Квадрат модуля: `|a|² = re² + im²` (без извлечения корня). */
export function modulusSquared(a: Complex): number {
  return a.re * a.re + a.im * a.im;
}

/** Модуль (абсолютная величина): `|a| = √(re² + im²)`. */
export function modulus(a: Complex): number {
  return Math.hypot(a.re, a.im);
}

/** Псевдоним {@link modulus} — абсолютная величина комплексного числа. */
export const abs = modulus;

/** Аргумент (фаза) комплексного числа в радианах: `atan2(im, re)` ∈ (−π, π]. */
export function argument(a: Complex): number {
  return Math.atan2(a.im, a.re);
}

/** Деление: `a / b`. Бросает ошибку при делении на ноль. */
export function divide(a: Complex, b: Complex): Complex {
  const denom = modulusSquared(b);
  if (denom === 0) {
    throw new Error('Complex division by zero');
  }
  return {
    re: (a.re * b.re + a.im * b.im) / denom,
    im: (a.im * b.re - a.re * b.im) / denom,
  };
}

/** Обратное значение: `1 / a`. Бросает ошибку при `a = 0`. */
export function reciprocal(a: Complex): Complex {
  const denom = modulusSquared(a);
  if (denom === 0) {
    throw new Error('Complex reciprocal of zero');
  }
  return { re: a.re / denom, im: -a.im / denom };
}

/** Комплексная экспонента: `e^a = e^{re}(cos(im) + i·sin(im))`. */
export function exp(a: Complex): Complex {
  const r = Math.exp(a.re);
  return { re: r * Math.cos(a.im), im: r * Math.sin(a.im) };
}

/**
 * Приблизительное равенство двух комплексных чисел в пределах допуска.
 * @param tolerance абсолютный допуск на модуль разности (по умолчанию `1e-9`)
 */
export function complexEquals(a: Complex, b: Complex, tolerance = 1e-9): boolean {
  return Math.hypot(a.re - b.re, a.im - b.im) <= tolerance;
}
